import type { DateRange } from '@daypicker/react';
import { addDays, differenceInCalendarDays, format, subDays } from 'date-fns';
import { Download } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import toast from 'react-hot-toast';
import { AnalyticsDashboard } from '@/components/analytics/AnalyticsDashboard';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { Button } from '@/components/ui/NewButton';
import {
  type Granularity,
  useAnalyticsOverview,
  useMatterAnalytics,
  useUserAnalytics,
} from '@/hooks/useAnalytics';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { AdminTabContainer } from './ShellTabs';

const GRANULARITIES = ['day', 'week', 'month'] as const;

// Each granularity doubles as a range preset, mirroring the picker's footer presets.
const GRANULARITY_RANGE_DAYS: Record<Granularity, number> = {
  day: 7,
  week: 30,
  month: 90,
};

const rangeForGranularity = (granularity: Granularity): { from: Date; to: Date } => ({
  from: subDays(new Date(), GRANULARITY_RANGE_DAYS[granularity]),
  to: new Date(),
});

/** The granularity whose preset range is currently selected, or null for a custom range. */
const granularityForRange = (from: Date, to: Date): Granularity | null =>
  GRANULARITIES.find((granularity) => {
    const preset = rangeForGranularity(granularity);
    return (
      differenceInCalendarDays(preset.from, from) === 0 &&
      differenceInCalendarDays(preset.to, to) === 0
    );
  }) ?? null;

/** Bucket size for a custom range, so wide ranges don't render hundreds of points. */
const granularityFromRangeLength = (from: Date, to: Date): Granularity => {
  const days = differenceInCalendarDays(to, from);
  if (days > 90) return 'month';
  if (days > 31) return 'week';
  return 'day';
};

/**
 * Firm analytics dashboard as an Admin hub tab.
 * Reports Export button to the parent so it can live in the shell header.
 */
export function AnalyticsTab({
  onHeaderActionsChange,
}: {
  onHeaderActionsChange?: (actions: ReactNode | null) => void;
}) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => rangeForGranularity('month'));
  const [isExporting, setIsExporting] = useState(false);

  const effectiveFrom = dateRange?.from ?? subDays(new Date(), 30);
  const effectiveTo = dateRange?.to ?? new Date();

  // A custom range leaves every button unpressed, but the charts still need a bucket size.
  const selectedGranularity = granularityForRange(effectiveFrom, effectiveTo);
  const granularity = selectedGranularity ?? granularityFromRangeLength(effectiveFrom, effectiveTo);

  const { data: unfilteredOverview } = useAnalyticsOverview({
    dateFrom: effectiveFrom,
    dateTo: effectiveTo,
    granularity,
    matterGuid: null,
    userGuid: null,
  });

  const { data: usersData, isLoading: usersLoading } = useUserAnalytics(
    { dateFrom: effectiveFrom, dateTo: effectiveTo, granularity },
  );

  const { data: mattersData, isLoading: mattersLoading } = useMatterAnalytics(
    { dateFrom: effectiveFrom, dateTo: effectiveTo, granularity },
  );

  const exportFrom = format(effectiveFrom, 'yyyy-MM-dd');
  const exportTo = format(effectiveTo, 'yyyy-MM-dd');

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const params: Record<string, string> = {
        format: 'pdf',
        date_from: exportFrom,
        date_to: exportTo,
        granularity,
      };

      const response = await backendClient.post('/analytics/export', params, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crimson-analytics-${exportFrom}-to-${exportTo}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to export analytics');
    } finally {
      setIsExporting(false);
    }
  }, [exportFrom, exportTo, granularity]);

  const headerActions = useMemo(() => (
    <Button
      variant="outline"
      size="sm"
      icon={Download}
      text="Export"
      loading={isExporting}
      disabled={isExporting}
      onClick={handleExport}
    />
  ), [handleExport, isExporting]);

  useEffect(() => {
    onHeaderActionsChange?.(headerActions);
    return () => onHeaderActionsChange?.(null);
  }, [headerActions, onHeaderActionsChange]);

  return (
    <AdminTabContainer>
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
        <DateRangePicker
          initialValue={dateRange}
          onChange={setDateRange}
          disabledDates={(date) => date > addDays(new Date(), 1)}
          footerPresets={['last7Days', 'last30Days', 'last90Days']}
          showTopControls
          triggerClassName="w-full justify-start sm:w-70"
        />

        <fieldset className="ml-auto flex overflow-hidden rounded-md border border-input">
          <legend className="sr-only">Granularity</legend>
          {GRANULARITIES.map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={selectedGranularity === g}
              onClick={() => setDateRange(rangeForGranularity(g))}
              className={cn(
                'px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-primary',
                selectedGranularity === g
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-gray-600 hover:bg-gray-100',
              )}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </fieldset>
      </div>

      <AnalyticsDashboard
        dateFrom={effectiveFrom}
        dateTo={effectiveTo}
        granularity={granularity}
        unfilteredActiveUsersData={unfilteredOverview?.active_users_over_time}
        usersData={usersData}
        usersLoading={usersLoading}
        mattersData={mattersData}
        mattersLoading={mattersLoading}
      />
    </AdminTabContainer>
  );
}
